import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from './api';
import { dayKey } from './pnl';
import { FocusToday, noteHasContent } from './Routine';
import { NOTE_TYPES, noteType as typeOf } from './sessionNotes';
import { RESET_FEELINGS, ResetEditor, ResetOverlay } from './Reset';
import RiskMeter from './RiskMeter';
import { ExperimentLine } from './ExperimentPanel';
import { currentLocale } from './langState';
import { useI18n } from './i18n';
import { trackEvent } from './analytics';
import { PositionSize } from './Tools';
import { SessionEventAlert } from './MarketCalendar';
import { appendPhrase, MicButton } from './speech';
import { TradeIdeasWatch } from './TradeIdeas';
import { BehaviorAlert } from './BehaviorInsights';

const TIMER_KEY = 'mm-timeout';
const LAST_TIMEOUT_KEY = 'mm-timeout-last-start';

function readLastTimeout() {
  try {
    return Number(localStorage.getItem(LAST_TIMEOUT_KEY)) || 0;
  } catch {
    return 0;
  }
}
const TIMEOUTS = [5, 15, 30];

function readTimer() {
  try {
    const t = JSON.parse(localStorage.getItem(TIMER_KEY));
    return t && t.end > Date.now() ? t : null;
  } catch {
    return null;
  }
}

function writeTimer(t) {
  try {
    if (t) localStorage.setItem(TIMER_KEY, JSON.stringify(t));
    else localStorage.removeItem(TIMER_KEY);
  } catch {
    // Not saved in private mode; the timer still runs on this page.
  }
}

function formatCountdown(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function timeOf(date) {
  return new Date(date).toLocaleTimeString(currentLocale(), { hour: 'numeric', minute: '2-digit' });
}

// A soft three-note chime when a timeout ends.
function chime(ctx) {
  if (!ctx) return;
  try {
    [0, 0.25, 0.5].forEach((delay, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = [660, 880, 990][i];
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + delay + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + 0.6);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.65);
    });
  } catch {
    // Sound isn't essential.
  }
}

// Keeps the screen from dimming or locking while the session page is open.
function useWakeLock() {
  const [active, setActive] = useState(false);
  useEffect(() => {
    if (!('wakeLock' in navigator)) return undefined;
    let lock = null;
    let cancelled = false;
    async function acquire() {
      try {
        lock = await navigator.wakeLock.request('screen');
        if (cancelled) {
          lock.release();
          return;
        }
        setActive(true);
        lock.addEventListener('release', () => setActive(false));
      } catch {
        setActive(false);
      }
    }
    function onVisible() {
      if (document.visibilityState === 'visible') acquire();
    }
    acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (lock) lock.release().catch(() => {});
    };
  }, []);
  return active;
}

// The position size calculator in a pop-up, so the session keeps running underneath.
function CalculatorModal({ onClose }) {
  const { t } = useI18n();
  const dialog = useRef(null);
  useEffect(() => {
    trackEvent('Position calculator');
    dialog.current?.focus();
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={dialog} tabIndex={-1} className="modal modal-wide calc-modal" role="dialog" aria-modal="true" aria-labelledby="calc-title">
        <div className="modal-header">
          <h2 id="calc-title">{t('Position size & R:R')}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>
        <p className="hint">{t('Your contract, risk and commission are remembered, so next time you only type entry, stop and target.')}</p>
        <PositionSize />
      </div>
    </div>
  );
}

// `sampleDate`: in the demo, a completed sample day to show while today's session is still empty.
function Session({ notes, setNotes, focusMode, onToggleFocus, onSessionExpired, sampleDate, accounts = [], trades = [], selected = 'all', experiments = [], onNewTrades }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [now, setNow] = useState(() => new Date());
  const [routine, setRoutine] = useState(null);
  const [timer, setTimer] = useState(readTimer);
  const [timeoutOver, setTimeoutOver] = useState(false);
  const [lastTimeoutStart, setLastTimeoutStart] = useState(readLastTimeout);
  const [draft, setDraft] = useState('');
  const [noteType, setNoteType] = useState('thought');
  const [error, setError] = useState('');
  const [resetFor, setResetFor] = useState(null);
  const [editingReset, setEditingReset] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const closeCalc = useCallback(() => setCalcOpen(false), []);
  const [vision, setVision] = useState('');
  const audio = useRef(null);
  const noteBox = useRef(null);
  const awake = useWakeLock();
  const today = dayKey(now);
  const [sampleOpen, setSampleOpen] = useState(true);
  const todayHasLog = Boolean(notes[today] && notes[today].sessionLog && notes[today].sessionLog.length);
  const sample = Boolean(sampleOpen && sampleDate && sampleDate !== today && notes[sampleDate] && !todayHasLog);
  const note = notes[sample ? sampleDate : today] || {};
  const log = note.sessionLog || [];
  const taps = note.sessionTrades || [];

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Synced accounts bring trades in during the day: check every 2 minutes while this page
  // is open, so the behavior check below sees them.
  useEffect(() => {
    if (!onNewTrades || sampleDate) return undefined;
    const check = () => {
      if (document.visibilityState !== 'visible') return;
      api(`/trades?since=${encodeURIComponent(new Date(Date.now() - 36 * 3600000).toISOString())}`).then(onNewTrades).catch(() => {});
    };
    const id = setInterval(check, 120000);
    return () => clearInterval(id);
  }, [onNewTrades, sampleDate]);

  useEffect(() => {
    api('/routine')
      .then(setRoutine)
      .catch((err) => {
        if (err.status === 401) onSessionExpired();
      });
    api('/goals').then((g) => setVision(g.vision || '')).catch(() => {});
  }, [onSessionExpired]);

  // Timeout finished: chime, show a message for a bit, and clear it.
  const remaining = timer ? timer.end - now.getTime() : 0;
  useEffect(() => {
    if (timer && remaining <= 0) {
      setTimer(null);
      writeTimer(null);
      setTimeoutOver(true);
      chime(audio.current);
      const id = setTimeout(() => setTimeoutOver(false), 15000);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [timer, remaining]);

  // Show the countdown in the browser tab while a timeout runs.
  useEffect(() => {
    const original = document.title;
    if (timer) document.title = `⏸ ${t('{time} timeout', { time: formatCountdown(remaining) })} · Market Matrix`;
    return () => {
      document.title = original;
    };
  });

  function startTimeout(minutes) {
    trackEvent('Timeout');
    // Creating the audio context on a click lets the end-of-timeout chime play later.
    try {
      if (!audio.current) audio.current = new (window.AudioContext || window.webkitAudioContext)();
      audio.current.resume();
    } catch {
      audio.current = null;
    }
    const next = { end: Date.now() + minutes * 60000, minutes };
    setTimer(next);
    writeTimer(next);
    setLastTimeoutStart(Date.now());
    try {
      localStorage.setItem(LAST_TIMEOUT_KEY, String(Date.now()));
    } catch {
      // Fine without it; the loss warning may just show again after a reload.
    }
    setTimeoutOver(false);
  }

  function cancelTimeout() {
    setTimer(null);
    writeTimer(null);
  }

  async function save(changes) {
    // Anything typed or tapped belongs to today, so leave the sample day.
    setSampleOpen(false);
    setError('');
    setNotes((prev) => ({ ...prev, [today]: { ...(prev[today] || { date: today }), ...changes } }));
    try {
      const { linkedTrades, ...saved } = await api(`/notes/${today}`, { method: 'PUT', body: changes });
      if (linkedTrades && linkedTrades.length) window.dispatchEvent(new CustomEvent('mm-trade-notes', { detail: linkedTrades }));
      setNotes((prev) => {
        const next = { ...prev };
        if (noteHasContent(saved)) next[today] = saved;
        else delete next[today];
        return next;
      });
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(t("Couldn't save. Check your connection; your note is still on screen."));
    }
  }

  function addNote(e) {
    if (e) e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    save({ sessionLog: [...log, { time: new Date().toISOString(), type: noteType, text }] });
    setDraft('');
    setNoteType('thought');
  }

  // Hitting the brakes: open the reset screen and note it in today's log.
  function openReset(feeling) {
    setResetFor(feeling);
    save({ sessionLog: [...log, { time: new Date().toISOString(), type: 'feeling', feeling: feeling.id, text: t('Hit the reset button: {feeling}', { feeling: t(feeling.label) }) }] });
  }

  function resetTimeout(minutes) {
    setResetFor(null);
    startTimeout(minutes);
  }

  function resetWrite() {
    const feeling = resetFor;
    setResetFor(null);
    setNoteType('feeling');
    setDraft(t(`Feeling ${feeling.id.toLowerCase()} because `));
    setTimeout(() => {
      const box = noteBox.current;
      if (box) {
        box.focus();
        box.setSelectionRange(box.value.length, box.value.length);
        box.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
  }

  function removeNote(index) {
    save({ sessionLog: log.filter((_, i) => i !== index) });
  }

  function tap(result) {
    save({ sessionTrades: [...taps, { time: new Date().toISOString(), result }] });
  }

  function undoTap() {
    save({ sessionTrades: taps.slice(0, -1) });
  }

  const wins = taps.filter((x) => x.result === 'win').length;
  const losses = taps.length - wins;
  let lossStreak = 0;
  for (let i = taps.length - 1; i >= 0 && taps[i].result === 'loss'; i--) lossStreak += 1;
  // Once a timeout has been taken after the latest loss, the warning has done its job.
  const lastLoss = lossStreak ? new Date(taps[taps.length - 1].time).getTime() : 0;
  const showLossWarning = lossStreak >= 2 && !timer && lastLoss > lastTimeoutStart;
  const rules = routine ? routine.rules : [];
  const lossRule = rules.find((r) => /(loss|p[ée]rdida)/i.test(r) && /(2|two|dos)/i.test(r));

  return (
    <div className="session">
      {sample && (
        <div className="session-sample" role="status">
          <p>
            <strong>{t('Sample session')}</strong>{' '}
            {t('This is a completed session from {day}: the plan, the time-stamped notes and the reset button tap. Today\u2019s session starts empty.',
              { day: new Date(`${sampleDate}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'long', month: 'long', day: 'numeric' }) })}
          </p>
          <button type="button" className="secondary small" onClick={() => setSampleOpen(false)}>{t("Start today's session")}</button>
        </div>
      )}
      <div className="session-top">
        <div className="session-heading">
          <p className="eyebrow">{t('Live session')}</p>
          <h1 className="routine-title">{t('Trade your plan')}</h1>
          <p className="subtitle">
            {now.toLocaleDateString(currentLocale(), { weekday: 'long', month: 'long', day: 'numeric' })}
            {awake && <span className="awake-badge" title={t("Your screen won't dim or lock while this page is open")}>{t('Screen stays on')}</span>}
          </p>
        </div>

        <div className="session-clock-card">
          <div className="session-clock" aria-live="off">
            {now.toLocaleTimeString(currentLocale(), { hour: 'numeric', minute: '2-digit', second: '2-digit' })}
          </div>
          <div className="timeout-buttons">
            <span className="timeout-label">{t('Take a timeout')}</span>
            {TIMEOUTS.map((m) => (
              <button key={m} type="button" className="secondary small" onClick={() => startTimeout(m)}>
                {t('{n} min', { n: m })}
              </button>
            ))}
          </div>
          <button type="button" className="calc-open-button" onClick={() => setCalcOpen(true)}>
            <span aria-hidden="true">📐</span> {t('Calculate R:R & size')}
          </button>
        </div>
      </div>

      <RiskMeter accounts={accounts} trades={trades} selected={selected} />

      <section className="feelings-bar" aria-label={t('Reset buttons')}>
        <span className="feelings-prompt">{t('Feeling it? Tap before you act:')}</span>
        <div className="feelings-buttons">
          {RESET_FEELINGS.map((f) => (
            <button key={f.id} type="button" className="feeling-button" onClick={() => openReset(f)}>
              <span aria-hidden="true">{f.face}</span> {t(f.label)}
            </button>
          ))}
        </div>
      </section>

      <SessionEventAlert />

      <div className="session-toolbar">
        <button type="button" className="link-button" onClick={onToggleFocus}>
          {focusMode ? t('Show sidebars') : t('Focus mode: hide sidebars')}
        </button>
      </div>

      {timer && (
        <div className="timeout-banner" role="status">
          <span className="timeout-count">{formatCountdown(remaining)}</span>
          <div>
            <strong>{t('Timeout: no new trades until {time}', { time: timeOf(timer.end) })}</strong>
            <span>{t("Step away from the charts. Breathe, stretch, or write down what you're feeling.")}</span>
          </div>
          <button type="button" className="secondary small" onClick={cancelTimeout}>{t('End timeout')}</button>
        </div>
      )}
      {!timer && timeoutOver && (
        <div className="timeout-banner done" role="status">
          <span className="timeout-count">✓</span>
          <div>
            <strong>{t('Timeout over')}</strong>
            <span>{t('Check your rules and your plan before your next trade.')}</span>
          </div>
        </div>
      )}
      <BehaviorAlert trades={trades} accounts={accounts} notes={notes} selected={selected} onTimeout={startTimeout} />
      {showLossWarning && (
        <div className="loss-warning" role="alert">
          <strong>🛑 {t('{n} losses in a row.', { n: lossStreak })}</strong>{' '}
          {lossRule ? t('Your rule: “{rule}”', { rule: lossRule }) : t('This is when tilt and revenge trading start.')}
          <button type="button" className="small" onClick={() => startTimeout(15)}>{t('Start a 15-minute timeout')}</button>
        </div>
      )}

      <div className="session-grid">
        <div className="session-col">
          <div className="routine-block rules-block session-rules">
            <span className="routine-block-title">{t('My rules')}</span>
            {rules.length ? (
              <ol className="rules-list">
                {rules.map((rule, i) => (
                  <li key={rule}>
                    <span className="rule-number">{t('Rule {n}', { n: i + 1 })}</span>
                    <span className="rule-text">{rule}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="sidebar-muted">{t('Add your rules in Morning prep.')}</p>
            )}
          </div>

          <ExperimentLine experiments={experiments} />
          <FocusToday notes={notes} date={today} />

          <div className="routine-block session-plan">
            <div className="routine-block-head">
              <span className="routine-block-title">{t("Today's plan")}</span>
              <button type="button" className="link-button" onClick={() => navigate('/app/morning')}>{t('Edit in Morning prep')}</button>
            </div>
            {note.plan ? <p className="session-plan-text">{note.plan}</p> : <p className="sidebar-muted">{t('No plan written for today.')}</p>}
          </div>
          <TradeIdeasWatch ideas={note.ideas || []} trades={trades} onChange={(ideas) => save({ ideas })} />
        </div>

        <div className="session-col">
          <form className="routine-block session-notes" onSubmit={addNote}>
            <span className="routine-block-title">{t('What are you seeing and thinking?')}</span>
            <div className="note-types" role="group" aria-label={t('Note type')}>
              {NOTE_TYPES.map((type) => (
                <button key={type.id} type="button" className={`note-type${noteType === type.id ? ' active' : ''}`}
                  aria-pressed={noteType === type.id} onClick={() => setNoteType(type.id)}>
                  <span aria-hidden="true">{type.icon}</span> {t(type.label)}
                </button>
              ))}
            </div>
            <textarea ref={noteBox} rows="3" value={draft} maxLength={1000}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) addNote(e);
              }}
              placeholder={t('e.g. Watching 7,795 for a reclaim. Want a 5-min close above before I enter. Press Enter to save.')} />
            <div className="session-notes-actions">
              <span className="field-hint">{t('Enter saves · Shift+Enter for a new line')}</span>
              <MicButton onText={(phrase) => setDraft((d) => appendPhrase(d, phrase))} />
              <button type="submit" className="small" disabled={!draft.trim()}>{t('Add note')}</button>
            </div>
            {error && <p className="error">{error}</p>}

            {log.length > 0 ? (
              <ol className="session-log">
                {[...log].map((entry, i) => ({ entry, i })).reverse().map(({ entry, i }) => {
                  const type = typeOf(entry.type);
                  return (
                    <li key={`${entry.time}-${i}`} className={`log-${entry.type}`}>
                      <span className="log-time">{timeOf(entry.time)}</span>
                      <span className="log-type" title={t(type.label)} aria-label={t(type.label)}>{type.icon}</span>
                      <span className="log-text">
                        {entry.text}
                        {entry.tradeId && <span className="log-linked" title={t('Added to the trade’s notes')}>🔗 {t('In the trade’s notes')}</span>}
                      </span>
                      <button type="button" className="icon-button remove" onClick={() => removeNote(i)} aria-label={t('Delete note')}>&times;</button>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="sidebar-muted">{t('Your notes for today will appear here, newest first.')}</p>
            )}
          </form>

          <div className="routine-block session-tracker">
            <div className="routine-block-head">
              <span className="routine-block-title">{t('Trades this session')} <em>{t('(optional)')}</em></span>
              {taps.length > 0 && <button type="button" className="link-button" onClick={undoTap}>{t('Undo')}</button>}
            </div>
            <div className="tracker-row">
              <span className="tracker-count"><strong className="gain">{wins}</strong> {t('W')}</span>
              <span className="tracker-count"><strong className="loss">{losses}</strong> {t('L')}</span>
              <button type="button" className="secondary small tracker-win" onClick={() => tap('win')}>{t('+ Win')}</button>
              <button type="button" className="secondary small tracker-loss" onClick={() => tap('loss')}>{t('+ Loss')}</button>
            </div>
            <span className="field-hint">{t('Tap after each trade to get a reminder when losses start stacking up.')}</span>
          </div>
        </div>
      </div>

      {resetFor && (
        <ResetOverlay
          feeling={resetFor}
          customActions={routine ? routine.resetActions || [] : []}
          customQuotes={routine ? routine.quotes || [] : []}
          vision={vision}
          onTimeout={resetTimeout}
          onWrite={resetWrite}
          onClose={() => setResetFor(null)}
          onCustomize={() => setEditingReset(true)}
        />
      )}
      {calcOpen && <CalculatorModal onClose={closeCalc} />}
      {editingReset && routine && (
        <ResetEditor
          routine={routine}
          onSaved={(saved) => {
            setRoutine(saved);
            setEditingReset(false);
          }}
          onClose={() => setEditingReset(false)}
          onSessionExpired={onSessionExpired}
        />
      )}

      <div className="routine-footer">
        <button type="button" className="secondary" onClick={() => navigate('/app/morning')}>&larr; {t('Morning prep')}</button>
        <button type="button" className="end-day-button" onClick={() => navigate('/app/reflection')}>
          {t('Done trading? End my day')} &rarr;
        </button>
      </div>
    </div>
  );
}

export default Session;
